import {
    Alert,
    Dialog,
    Heading,
    Pane,
    Spinner,
    Strong,
    Text,
} from 'evergreen-ui';
import { Component } from 'react';
import Request from '../../../util/Api';
import Config from '../Config';

const POLL_INTERVAL_MS = 2000;

const STATUS_LABELS = {
    pending: 'In asteptare',
    preloading: 'Preincarcare carti',
    syncing: 'Sincronizare',
    done: 'Finalizat',
    error: 'Eroare',
};

const STATUS_COLORS = {
    pending: '#8f95b2',
    preloading: '#1070ca',
    syncing: '#1070ca',
    done: '#52bd95',
    error: '#d14343',
};

class ProgressBar extends Component {
    render() {
        const { value = 0, max = 0, color = '#1070ca' } = this.props;
        const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
        return (
            <Pane
                background="tint2"
                height={8}
                borderRadius={4}
                overflow="hidden"
                width="100%"
            >
                <Pane
                    background={color}
                    height="100%"
                    width={`${pct}%`}
                    transition="width 0.3s ease"
                />
            </Pane>
        );
    }
}

class PriceResyncProgressModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            progress: null,
            loading: false,
            error: null,
        };
        this._pollTimer = null;
        this._mounted = false;
    }

    componentDidMount() {
        this._mounted = true;
        if (this.props.isShown) {
            this.fetchStatus();
            this.startPolling();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.isShown && !prevProps.isShown) {
            this.fetchStatus();
            this.startPolling();
        } else if (!this.props.isShown && prevProps.isShown) {
            this.stopPolling();
        }
    }

    componentWillUnmount() {
        this._mounted = false;
        this.stopPolling();
    }

    startPolling = () => {
        this.stopPolling();
        this._pollTimer = setInterval(this.fetchStatus, POLL_INTERVAL_MS);
    };

    stopPolling = () => {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
    };

    fetchStatus = async () => {
        try {
            const res = await Request.get(Config.Endpoints.resyncPricesStatus);
            if (!this._mounted) return;
            const progress = res && res.progress ? res.progress : null;
            this.setState({ progress, error: null });

            // Auto-stop polling once the run has finished and the modal is
            // still showing the completed state.
            if (progress && !progress.running) {
                this.stopPolling();
            }
        } catch (err) {
            if (!this._mounted) return;
            this.setState({
                error: 'Nu s-a putut obtine progresul sincronizarii',
            });
        }
    };

    handleClose = () => {
        this.stopPolling();
        this.props.onClose && this.props.onClose();
    };

    renderGameProgress(game) {
        const status = game.status || 'pending';
        const color = STATUS_COLORS[status] || '#1070ca';

        const isPreloading = status === 'preloading';
        const value = isPreloading ? game.preloadedIds : game.processedPrices;
        const max = isPreloading ? game.totalIds : game.totalPrices;

        return (
            <Pane key={game.gameId} marginBottom={12}>
                <Pane
                    display="flex"
                    justifyContent="space-between"
                    marginBottom={4}
                >
                    <Strong>{`Joc ${game.gameId}`}</Strong>
                    <Text color={color}>{STATUS_LABELS[status] || status}</Text>
                </Pane>
                <ProgressBar value={value} max={max} color={color} />
                <Pane
                    display="flex"
                    justifyContent="space-between"
                    marginTop={4}
                >
                    <Text size={300} color="muted">
                        {isPreloading
                            ? `Preincarcat ${game.preloadedIds}/${game.totalIds} ID-uri`
                            : `Procesat ${game.processedPrices}/${game.totalPrices} preturi`}
                    </Text>
                    <Text size={300} color="muted">
                        {`Salvate: ${game.savedEntries} | Sarit fara card: ${game.skippedNoCard} | Fara extensie: ${game.skippedNoExpansion}`}
                    </Text>
                </Pane>
                {game.error && (
                    <Alert intent="danger" marginTop={6} title="Eroare">
                        {game.error}
                    </Alert>
                )}
            </Pane>
        );
    }

    renderBody() {
        const { progress, error } = this.state;

        if (error) {
            return <Alert intent="danger" title="Eroare">{error}</Alert>;
        }

        if (!progress) {
            return (
                <Pane display="flex" alignItems="center" justifyContent="center" padding={20}>
                    <Spinner size={24} />
                    <Text marginLeft={12}>Se incarca progresul...</Text>
                </Pane>
            );
        }

        const totalGames = progress.games.length;
        const doneGames = progress.games.filter((g) => g.status === 'done').length;
        const overallColor = progress.error
            ? '#d14343'
            : progress.running
            ? '#1070ca'
            : '#52bd95';

        return (
            <Pane>
                <Pane marginBottom={16}>
                    <Pane display="flex" justifyContent="space-between" marginBottom={4}>
                        <Heading size={400}>Progres general</Heading>
                        <Text color={overallColor}>
                            {progress.running
                                ? `In curs (joc ${progress.currentGameId || '-'})`
                                : progress.error
                                ? 'Eroare'
                                : progress.finishedAt
                                ? 'Finalizat'
                                : 'Inactiv'}
                        </Text>
                    </Pane>
                    <ProgressBar
                        value={doneGames}
                        max={totalGames}
                        color={overallColor}
                    />
                    <Text size={300} color="muted">
                        {`${doneGames}/${totalGames} jocuri finalizate`}
                    </Text>
                </Pane>

                {progress.error && (
                    <Alert intent="danger" title="Eroare globala" marginBottom={12}>
                        {progress.error}
                    </Alert>
                )}

                {progress.games.map((g) => this.renderGameProgress(g))}
            </Pane>
        );
    }

    render() {
        const { progress } = this.state;
        const isRunning = progress && progress.running;

        return (
            <Dialog
                isShown={this.props.isShown}
                title="Resincronizare preturi"
                onCloseComplete={this.handleClose}
                hasFooter={true}
                hasCancel={false}
                confirmLabel={isRunning ? 'Ruleaza in fundal' : 'Inchide'}
                onConfirm={this.handleClose}
                shouldCloseOnEscapePress={!isRunning}
                shouldCloseOnOverlayClick={!isRunning}
                width={720}
            >
                {this.renderBody()}
            </Dialog>
        );
    }
}

export default PriceResyncProgressModal;
